import { isResource } from '@medplum/core'
import type { Practitioner, Reference, Task } from '@medplum/fhirtypes'
import {
  codeableConceptHasSystem,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  SIGNATURE_REQUEST_TYPE,
  SIGNATURE_TASK_RECIPIENT_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import { type IDTRole, isIDTRole } from '#src/models/care-teams/constants.ts'
import type { SignatureTaskCode } from '#src/models/tasks/constants.ts'
import {
  StandardTask,
  type StandardTaskParams,
} from '#src/models/tasks/standard/index.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now, parseReferenceString } from '#src/utils.ts'

export abstract class SignatureTask extends StandardTask {
  static readonly RESOURCE_TYPE = 'Task'

  constructor(params: StandardTaskParams) {
    super(params)
    const recipientId = parseReferenceString(params.owner?.reference)?.id
    if (recipientId) {
      this.addTag({
        system: SIGNATURE_TASK_RECIPIENT_TAG_SYSTEM,
        code: recipientId,
      })
    }
  }

  sign(signer: Reference<Practitioner>): void {
    this.state = 'done'
    this.ownerPractitioner = signer
    this.completionDate = now()
  }

  removeSignature(): void {
    this.state = 'todo'
    this.completionDate = undefined
    if (this.recipient) {
      this.ownerPractitioner = this.recipient
    }
  }

  // The IDT seat this signature is for, from performerType. New tasks hold
  // just that role; legacy tasks may also carry the signer's other roles.
  // Undefined for signatures with no IDT seat (participant review, orders).
  get role(): IDTRole | undefined {
    return this.roles?.find(isIDTRole)
  }

  set role(role: IDTRole | undefined) {
    this.roles = role ? [role] : undefined
  }

  get signatureTaskCode(): SignatureTaskCode {
    return codeFromCodeableConcept(
      this.code,
      SIGNATURE_REQUEST_TYPE
    ) as SignatureTaskCode
  }

  static isInstance(value: unknown): value is Task {
    return (
      isResource(value) &&
      isOfType(value, 'Task') &&
      codeableConceptHasSystem(value.code, SIGNATURE_REQUEST_TYPE)
    )
  }

  static completedRatio(signatures: SignatureTask[]): string {
    const completed = signatures.filter((s) => s.isDone).length
    const total = signatures.length
    return `${completed}/${total}`
  }

  /**
   * Filter signature tasks to those originally requested of the given signer.
   * Keyed on a meta.tag stamped at construction; stable across sign-on-behalf.
   */
  static byRecipient(signer: Reference<Practitioner>): QueryTypes {
    const id = parseReferenceString(signer.reference)?.id
    invariant(id, 'Signer reference must include an id')
    return { _tag: `${SIGNATURE_TASK_RECIPIENT_TAG_SYSTEM}|${id}` }
  }
}
